import type { FirebaseServices } from './app.ts';
import type { Filter } from './query.ts';

export type Row = { id: string; data: Record<string, unknown> };

// Live reads (rules decide who may listen). Firestore loads lazily, so the
// returned function also cancels a listener that has not started yet.
function deferred(start: () => Promise<() => void>, onError: (error: Error) => void): () => void {
  let stop: (() => void) | null = null;
  let cancelled = false;
  start().then(unsubscribe => {
    if (cancelled) unsubscribe();
    else stop = unsubscribe;
  }, error => { if (!cancelled) onError(error as Error); });
  return () => {
    cancelled = true;
    stop?.();
  };
}

export function watchDocument(
  services: FirebaseServices,
  path: string,
  onData: (data: Record<string, unknown> | null) => void,
  onError: (error: Error) => void,
): () => void {
  return deferred(async () => {
    const [{ doc, onSnapshot }, firestore] = await Promise.all([import('firebase/firestore'), services.firestore()]);
    return onSnapshot(doc(firestore, path), snapshot => onData(snapshot.exists() ? snapshot.data() : null), onError);
  }, onError);
}

export function watchQuery(
  services: FirebaseServices,
  collectionPath: string,
  { filters = [], orderBy, limit = 100 }: { filters?: Filter[]; orderBy?: [field: string, direction: 'asc' | 'desc']; limit?: number },
  onData: (rows: Row[]) => void,
  onError: (error: Error) => void,
): () => void {
  return deferred(async () => {
    const [firestoreModule, firestore] = await Promise.all([import('firebase/firestore'), services.firestore()]);
    const { collection, limit: limitTo, onSnapshot, orderBy: order, query, where } = firestoreModule;
    const constraints = [
      ...filters.map(([field, op, value]) => where(field, op, value)),
      ...(orderBy ? [order(orderBy[0], orderBy[1])] : []),
      limitTo(limit),
    ];
    return onSnapshot(
      query(collection(firestore, collectionPath), ...constraints),
      snapshot => onData(snapshot.docs.map(item => ({ id: item.id, data: item.data() }))),
      onError,
    );
  }, onError);
}
